import {useCallback, useEffect, useRef} from 'react';
import {socketConnect, disconnect, socket} from '@dropDesk/data/clients/socket.client';
import {getToken} from '@dropDesk/data/data_source/token/token.datasource';
import {UserEntity} from '@dropDesk/domain/entities/user/user.entity';

interface UseSocketConnectionProps {
    user: UserEntity;
    origin: 'chat' | 'ticket';
    handleDisconnect: (temporary: boolean) => void;
    handleGetLastUpdates: () => void;
    onConnected?: () => void;
    enabled?: boolean;
    roomId?: string;
}

export const useSocketConnection = ({
    user,
    origin,
    handleDisconnect,
    handleGetLastUpdates,
    onConnected,
    enabled = true,
    roomId
}: UseSocketConnectionProps) => {
    const isConnectedRef = useRef(false);
    const hasCalledOnConnected = useRef(false);

    const onConnectedRef = useRef(onConnected);
    const handleDisconnectRef = useRef(handleDisconnect);
    const handleGetLastUpdatesRef = useRef(handleGetLastUpdates);
    const roomIdRef = useRef(roomId);

    useEffect(() => {
        onConnectedRef.current = onConnected;
        handleDisconnectRef.current = handleDisconnect;
        handleGetLastUpdatesRef.current = handleGetLastUpdates;
        roomIdRef.current = roomId;
    });

    const getRoomsToDisconnect = useCallback(() => {
        const roomsToDisconnect = ['message', 'internalChatMessage', user.id];
        if (roomIdRef.current) {
            roomsToDisconnect.push(roomIdRef.current);
        }
        return roomsToDisconnect;
    }, [user.id]);

    const connectSocket = useCallback(async (force = false) => {
        if (!force && (socket?.connected || isConnectedRef.current)) {
            console.log('Socket já conectado, reutilizando conexão');
            if (onConnectedRef.current && !hasCalledOnConnected.current) {
                hasCalledOnConnected.current = true;
                onConnectedRef.current();
            }
            return;
        }

        try {
            const token = await getToken();
            socketConnect(
                {authorization: `Bearer ${token}`, origin},
                (temporary) => handleDisconnectRef.current(temporary),
                () => handleGetLastUpdatesRef.current(),
                origin,
                user.id,
                'chat'
            );
            isConnectedRef.current = true;
            console.log(`Socket conectado para ${origin}`);

            if (onConnectedRef.current) {
                hasCalledOnConnected.current = true;
                onConnectedRef.current();
            }
        } catch (error) {
            console.error('Erro ao conectar socket:', error);
            throw error;
        }
    }, [origin, user.id]);

    const reconnect = useCallback(async () => {
        if (socket || isConnectedRef.current) {
            disconnect(getRoomsToDisconnect());
        }
        isConnectedRef.current = false;
        hasCalledOnConnected.current = false;
        await connectSocket(true);
    }, [connectSocket, getRoomsToDisconnect]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        connectSocket();

        return () => {
            if (isConnectedRef.current) {
                console.log(`Desconectando socket de ${origin}`);
                disconnect(getRoomsToDisconnect());
                isConnectedRef.current = false;
                hasCalledOnConnected.current = false;
            }
        };
    }, [enabled, user.id, origin, connectSocket, getRoomsToDisconnect]);

    return {
        isConnected: isConnectedRef.current,
        socket,
        reconnect
    };
};
